import { Typography } from '@mui/material'
import { BarDatum, ResponsiveBar } from '@nivo/bar'

interface Props {
  dataset: BarDatum[]
  height?: number
  title?: string
  subTitle?: string
  labels: { x: string; y: string }
  isHorizontal?: boolean
  extraTextPadding?: boolean
  labelTextColor?: string
  yFormat?: (e: number) => string
  adjustXAxisLabel?: boolean
}

/**
 * nivo BarChart Component
 */
export function BarChart({
  dataset,
  height = 300,
  title,
  labels,
  isHorizontal,
  extraTextPadding,
  labelTextColor = 'grey',
  yFormat = undefined,
  adjustXAxisLabel = false,
}: Props) {
  return (
    <section>
      {title && (
        <Typography variant="h6" component="h2" align="center">
          {title}
        </Typography>
      )}
      <div style={{ height }}>
        <ResponsiveBar
          layout={isHorizontal ? 'horizontal' : 'vertical'}
          data={dataset}
          keys={['y']}
          indexBy="x"
          margin={{ top: 50, right: 60, bottom: 50, left: extraTextPadding ? 350 : 60 }}
          padding={0.3}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={['#51247A']}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisTop={null}
          axisRight={null}
          isInteractive
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: labels.x,
            legendPosition: 'middle',
            legendOffset: adjustXAxisLabel ? 45 : 32,
            renderTick: adjustXAxisLabel ? HorizontalTick : undefined,
          }}
          enableLabel
          labelTextColor={labelTextColor}
          label={(d) => (!isHorizontal ? <tspan y={-6}>{d.value}</tspan> : d.value) as unknown as string}
          axisLeft={{
            format: yFormat,
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: labels.y,
            legendPosition: 'middle',
            legendOffset: extraTextPadding ? -330 : -40,
          }}
          role="application"
          ariaLabel={title}
        />
      </div>
    </section>
  )
}

/**
 * Returns a tick element that wraps text for the given number of lines and adds an ellipsis if the text can't fit. This can be passed to the renderTick method.
 */
type HorizontalTickProps = {
  textAnchor: string
  textBaseline: string
  value: string
  x: number
  y: number
}
const HorizontalTick = ({ textAnchor, textBaseline, value, x, y }: HorizontalTickProps) => {
  const MAX_LINE_LENGTH = 20
  const MAX_LINES = 3
  const lineHeight = 1.2
  const words = value.split(/\s+/)
  const lines = []
  let currentLine = ''

  for (let i = 0; i < words.length; i++) {
    const word = words[i]
    const proposedLine = `${currentLine}${currentLine ? ' ' : ''}${word}`

    if (proposedLine.length > MAX_LINE_LENGTH || lines.length === MAX_LINES - 1) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = proposedLine
    }
  }

  lines.push(currentLine)

  const splitValues = lines.map((line, i) => (
    <tspan key={i} dy={`${lineHeight}em`} x={0} style={{ fontFamily: 'sans-serif', fontSize: '7px' }}>
      {line}
    </tspan>
  ))

  return (
    <g transform={`translate(${x},${y + 10})`}>
      <text
        alignmentBaseline={textBaseline as React.SVGAttributes<SVGTextElement>['alignmentBaseline']}
        textAnchor={textAnchor as React.SVGAttributes<SVGTextElement>['textAnchor']}
      >
        {splitValues}
      </text>
    </g>
  )
}
